import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AxisSetExtremesEventCallbackFunction, Options, Point } from 'highcharts';
import Highcharts from 'highcharts/highstock';
import HighchartsReact, { HighchartsReactRefObject } from 'highcharts-react-official';
import { CrosshairPosition, TimelineHighchartsReactRefObject } from '../shared';
import flagIcon from './svg/flag.svg';
import flagsIcon from './svg/flags.svg';
import tagIcon from './svg/tag.svg';
import tagsIcon from './svg/tags.svg';

import 'highcharts/esm/modules/xrange.js';
import { useEventListener } from '@hooks-clean/use-event-listener';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';

export interface FlagData {
    date: Date;
    label: string;
}

interface IProps {
    type: 'Milestone' | 'Tag';
    ref?: React.Ref<HighchartsReactRefObject>;
    chartRefs: (HighchartsReactRefObject | null)[];
    startDate?: number;
    endDate?: number;
    startingExtremes: { min: number; max: number };
    data: FlagData[];
    synchronizeCrosshairs: (pos: CrosshairPosition | undefined) => void;
}

interface FlagPoint extends Point {
    title: string;
    isCluster: boolean;
    clusteredData: Point[];
}

const emptyRef = { current: null };

// Flag dates are calendar days, normalized to UTC midnight upstream. Render the marker at midday so the
// centred icon sits inside the day at every zoom level, instead of on the midnight boundary where it would
// appear to belong to - the previous day.
const MIDDAY_OFFSET_MS = 12 * 60 * 60 * 1000;

function formatDate(input: Date | string | number | null | undefined) {
    if (input == null) {
        return null;
    }

    // Make it a Date - the helper function accepts everything except number, but number can represent timestamp ticks
    const date = new Date(input);

    return stringDateWithoutTimeFromUtc(date, 'DD-MMM-yyyy');
}

function ellipsisifyTitle(title: string | undefined) {
    if (title == null) {
        return '';
    }

    if (title.length > 15) {
        return `${title.slice(0, 12)}...`;
    }
    return title;
}

const FlagChart = ({
    type,
    ref,
    chartRefs,
    startDate,
    endDate,
    startingExtremes,
    data,
    synchronizeCrosshairs,
}: IProps) => {
    const chartRef = useRef<TimelineHighchartsReactRefObject>(null);

    const markerSingle = useMemo(
        () => ({
            symbol: `url(${type === 'Milestone' ? flagIcon : tagIcon})`,
            height: 16,
            width: 16,
        }),
        [type],
    );
    const markerMultiple = useMemo(
        () => ({
            symbol: `url(${type === 'Milestone' ? flagsIcon : tagsIcon})`,
            height: 21,
            width: 24,
        }),
        [type],
    );

    // When mouse moves over chart, try to track position and draw crosshair
    useEventListener(chartRef.current?.container ?? emptyRef, 'mousemove', (e) => {
        const chart = chartRef.current?.chart;
        if (chart == null) {
            synchronizeCrosshairs(undefined);
            return;
        }

        // Get x position on plot area
        const plotX = e.offsetX - chart.plotLeft;
        // Make sure it's in-bounds
        if (plotX < 0 || plotX > chart.plotWidth) {
            synchronizeCrosshairs(undefined);
            return;
        }

        // Get min/max extreme of chart
        // It's an array, but it should presumably always have 1 element...
        const minExtremes = chart.xAxis.map((x) => x.getExtremes().min);
        const maxExtremes = chart.xAxis.map((x) => x.getExtremes().max);
        if (minExtremes.length === 0 || maxExtremes.length === 0) {
            synchronizeCrosshairs(undefined);
            return;
        }

        const minExtreme = Math.min(...minExtremes);
        const maxExtreme = Math.max(...maxExtremes);
        const extremesDelta = maxExtreme - minExtreme;

        // Get percentage of way x position is across the plot area.
        const percentage = plotX / chart.plotWidth;
        // Multiply the `extremesDelta` by this to get the value at the x position given the current extremes
        const value = new Date(minExtreme + Math.floor(extremesDelta * percentage));

        synchronizeCrosshairs({ plotX, value });
    });

    // When mouse leaves, remove crosshair
    useEventListener(chartRef.current?.container ?? emptyRef, 'mouseleave', () => {
        synchronizeCrosshairs(undefined);
    });

    const series = useMemo(
        () => [
            {
                type: 'scatter',
                marker: markerSingle,
                stickyTracking: false,
                cluster: {
                    enabled: true,
                    marker: markerMultiple,
                    layoutAlgorithm: {
                        distance: 200,
                    },
                    drillToCluster: true,
                    dataLabels: {
                        // Data label formatting is too restrictive here, so we do this manually in the chart render event (see below).
                        enabled: true,
                        align: 'left',
                        verticalAlign: 'middle',
                        padding: 15,
                        allowOverlap: true,
                        style: {
                            color: '#3E536E',
                            fontSize: '12px',
                        },
                    },
                },
                dataLabels: {
                    enabled: true,
                    animate: false,
                    formatter() {
                        const that = this as unknown as FlagPoint;

                        if (Array.isArray(that.title)) {
                            if (that.title.length > 2) {
                                return (
                                    `<b>${ellipsisifyTitle(that.title[0])}</b><br/>` +
                                    `<b>(${that.title.length - 1} more...)</b>`
                                );
                            }

                            return that.title.map((t) => `<b>${ellipsisifyTitle(t)}</b>`).join('<br/>');
                        }

                        return `${ellipsisifyTitle(that.title)}`;
                    },
                    align: 'left',
                    verticalAlign: 'middle',
                    padding: 15,
                    allowOverlap: true,
                    style: {
                        color: '#3E536E',
                        fontSize: '12px',
                    },
                },
                data: data.map((d) => ({
                    x: new Date(d.date).getTime() + MIDDAY_OFFSET_MS,
                    y: 1,
                    title: d.label,
                })),
            } satisfies Highcharts.SeriesOptionsType | Highcharts.UnknownSeriesOptions,
        ],
        [data, markerMultiple, markerSingle],
    );

    const afterSetExtremes: AxisSetExtremesEventCallbackFunction = useCallback(
        ({ min, max }) => {
            // Get defined charts from refs, get x axes from charts, set extremes of every x-axis
            chartRefs
                .filter((r) => r != null)
                .flatMap((r) => r.chart.axes)
                .filter((a) => a.isXAxis)
                .forEach((a) => {
                    a.setExtremes(min, max);
                });
        },
        [chartRefs],
    );

    // On mount, get `plotLeft` property of chart. This is so that the title doesn't overlap with the left y-axis labels
    const [chartPlotLeft, setChartPlotLeft] = useState<number>();
    useEffect(() => {
        setChartPlotLeft(chartRef?.current?.chart.plotLeft);
    }, []);

    const options = useMemo(
        () =>
            ({
                chart: {
                    zooming: {
                        type: 'x',
                        mouseWheel: false,
                    },
                    height: 35,
                    animation: false,
                    marginTop: 0,
                    marginBottom: 0,
                    events: {
                        load() {
                            const chart = this;

                            chart.axes
                                .filter((x) => x.isXAxis)
                                .forEach((x) => {
                                    x.setExtremes(startingExtremes.min, startingExtremes.max);
                                });
                        },
                        render() {
                            // Highchart's  data label formatting for clustered points is quite limiting, so we instead override the data label
                            // text according to our own logic here.
                            const chart = this;

                            chart.series.forEach((s) => {
                                s.points.forEach((point) => {
                                    const flagPoint = point as FlagPoint;
                                    if (flagPoint.isCluster) {
                                        const getText = (clusteredData: Point[]) => {
                                            if (clusteredData.length > 2) {
                                                return (
                                                    `<b>${ellipsisifyTitle(clusteredData[0].options.title)}</b><br/>` +
                                                    `<b>(${clusteredData.length - 1} more...)</b>`
                                                );
                                            }

                                            return clusteredData
                                                .map((p) => `<b>${ellipsisifyTitle(p.options.title)}</b>`)
                                                .join('<br/>');
                                        };

                                        // mutate label text here
                                        flagPoint.dataLabel?.attr({
                                            y: -12, // Ofset slightly as it will always be 2 lines.
                                            text: getText(flagPoint.clusteredData),
                                        });
                                    }
                                });
                            });
                        },
                    },
                },
                xAxis: {
                    panningEnabled: false,
                    type: 'datetime',
                    min: startDate,
                    max: endDate,
                    crosshair: false,
                    ordinal: false,
                    visible: false,
                    events: {
                        afterSetExtremes,
                    },
                },
                tooltip: {
                    animation: false,
                    snap: 10,
                    formatter() {
                        const that = this as unknown as FlagPoint;

                        // We have a simple tooltip for normal, non-clustered points.
                        if (!that.isCluster) {
                            return `<b>${that.title}</b><br/>${formatDate(that.x)}`;
                        }

                        // With clustered points, we need to show all titles, but group them by date.
                        const distinctDates = that.clusteredData.reduce((acc: Set<number>, point) => {
                            acc.add(point.x);
                            return acc;
                        }, new Set<number>());

                        const lines: string[] = [];

                        distinctDates.forEach((date) => {
                            const pointsForDate = that.clusteredData.filter((p) => p.x === date);
                            const titlesForDate = pointsForDate.map((p) => p.options.title);

                            lines.push(
                                `${stringDateWithoutTimeFromUtc(
                                    new Date(date),
                                    'DD-MMM-YYYY',
                                )}: <b>${titlesForDate.join(', ')}</b>`,
                            );
                        });

                        return lines.join('<br/>');
                    },
                    shared: true,
                    outside: true,
                },
                yAxis: [
                    {
                        height: `${100 / series.length}%`,
                        visible: true,
                        lineWidth: 1,
                        gridLineWidth: 0,
                        labels: {
                            enabled: false,
                        },
                        opposite: false,
                    },
                    {
                        lineWidth: 1,
                        opposite: true,
                    },
                ],
                series,
                rangeSelector: { enabled: false },
                exporting: { enabled: false },
                navigator: { enabled: false },
                scrollbar: { enabled: false },
            }) satisfies Options,
        [type, chartPlotLeft, startDate, endDate, afterSetExtremes, series, startingExtremes.min, startingExtremes.max],
    );

    return (
        <HighchartsReact
            ref={(el) => {
                if (ref != null) {
                    typeof ref === 'function' ? ref(el) : (ref.current = el);
                }
                chartRef.current = el;
            }}
            highcharts={Highcharts}
            constructorType="stockChart"
            options={options}
        />
    );
};

export default FlagChart;
